/**
 * Parses a Google Calendar event URL of the canonical form:
 *
 *     https://www.google.com/calendar/event?eid=<base64-blob>[&...]
 *
 * Returns `null` for any other shape.
 */
export type ParsedGoogleCalendarUrl = {
	eventId: string;
	calendarId: string;
	startTimeIso?: string;
};

const EVENT_ID_TIME_SUFFIX = /_(\d{8}T\d{6}Z)$/;

const decodeBase64Loose = (raw: string): string | null => {
	try {
		return Buffer.from(raw, 'base64').toString('utf-8');
	} catch {
		return null;
	}
};

const toIsoStartTime = (eventId: string): string | undefined => {
	const match = eventId.match(EVENT_ID_TIME_SUFFIX);
	if (!match) return undefined;
	const t = match[1];
	return `${t.slice(0, 4)}-${t.slice(4, 6)}-${t.slice(6, 11)}:${t.slice(11, 13)}:${t.slice(13, 15)}Z`;
};

const CANONICAL_HOSTNAME = 'www.google.com';
const CANONICAL_PATHNAME = '/calendar/event';

const matchesCanonicalShape = (parsed: URL): boolean =>
	parsed.protocol === 'https:' &&
	parsed.hostname === CANONICAL_HOSTNAME &&
	parsed.pathname === CANONICAL_PATHNAME;

/* eslint-disable-next-line @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const parseGoogleCalendarUrl = (url: string): ParsedGoogleCalendarUrl | null => {
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return null;
	}

	if (!matchesCanonicalShape(parsed)) return null;

	const eidBlob = parsed.searchParams.get('eid');
	if (!eidBlob) return null;

	const decoded = decodeBase64Loose(eidBlob);
	if (!decoded) return null;

	const [eventId, ownerEmail] = decoded.split(' ');
	if (!eventId || !ownerEmail) return null;

	return {
		eventId,
		calendarId: ownerEmail,
		startTimeIso: toIsoStartTime(eventId),
	};
};

/* eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports, @atlaskit/platform/one-value-export-per-file -- legacy onboarding baseline */
export const isGoogleCalendarUrl = (url: string): boolean => {
	try {
		const parsed = new URL(url);
		return matchesCanonicalShape(parsed) && parsed.searchParams.has('eid');
	} catch {
		return false;
	}
};
